import { ApplicationLayer, Row } from "@react-gcc-eds/core";
import cx from "classnames";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useCustomizationActions } from "../../../actions/customization-actions";
import {
  IInternalItemsRedirectionFilters,
  useInternalItemsFiltersActions
} from "../../../actions/internal-items-filters-actions";
import {
  IInternalOrdersRedirectionFilters,
  useInternalOrdersFiltersActions
} from "../../../actions/internal-orders-filters-actions";
import {
  IExternalOrdersRedirectionFilters,
  useOrdersFiltersActions
} from "../../../actions/orders-filters-actions";
import {
  IExternalItemsRedirectionFilters,
  usePOItemsFiltersActions
} from "../../../actions/po-items-filters-actions";
import { useSettingsActions } from "../../../actions/settings-actions";
import { IViewProperty, useStore } from "../../../store";
import { lowerCaseInitial } from "../../../utils";
import { useLocalization } from "../../../utils/localization";
import useAppMenu from "../../shared/menu";
import { useLoadingBar } from "../../shared/use-loading-bar";
import { EViewId } from "../../shared/view-ids";
import ConfirmedDeliveryDate from "./confirmed-delivery-date";
import DeliveryTimePrecision from "./delivery-precision";
import MaterialInventory from "./material-inventory";
import MeetRequestedDate from "./meet-requested-date";
import OrderAverageLeadTime from "./order-average-lead-time";
import OrderLeadTime from "./order-lead-time";
import OverallInternalOrderStatusBars from "./overall-internal-order-status-bars";
import OverallInternalOrderStatusDonut from "./overall-internal-order-status-donut";
import OverallOrderStatusBars from "./overall-order-status-bars";
import OverallOrderStatusDonut from "./overall-order-status-donut";
import PlannedDeliveryDate from "./planned-delivery-date";
import ShipmentCount from "./shipment-count";
import ShipmentDelivered from "./shipment-delivered";

export enum DashboardGraph {
  Orders,
  Items,
  InternalOrders,
  InternalItems
}

export interface IDashboardItemSelected {
  graph: DashboardGraph;
  filters:
    | IExternalOrdersRedirectionFilters
    | IInternalOrdersRedirectionFilters
    | IExternalItemsRedirectionFilters
    | IInternalItemsRedirectionFilters;
}

export enum DashboardTileAlignment {
  LEFT,
  RIGHT,
  FULL
}

interface IDashboard {
  id: string;
  component: any;
  redirectToOrders: boolean;
  redirectToItems: boolean;
}

const isPropHidden = (customization: IViewProperty[], prop: string) =>
  (customization.find(p => p.propertyName === prop) || { hide: true }).hide;

const dashboardsConfig = (
  ordersCustomization: IViewProperty[],
  itemsCustomization: IViewProperty[],
  internalOrdersCustomization: IViewProperty[],
  internalItemsCustomization: IViewProperty[]
): IDashboard[] => [
  {
    id: "OrderStatusBars",
    component: OverallOrderStatusBars,
    redirectToOrders: !isPropHidden(ordersCustomization, "StatusLabel"),
    redirectToItems: !isPropHidden(itemsCustomization, "StatusLabel")
  },
  {
    id: "OrderStatusDonuts",
    component: OverallOrderStatusDonut,
    redirectToOrders: !isPropHidden(ordersCustomization, "StatusLabel"),
    redirectToItems: !isPropHidden(itemsCustomization, "StatusLabel")
  },
  {
    id: "MeetRequestedDate",
    component: MeetRequestedDate,
    redirectToOrders:
      !isPropHidden(ordersCustomization, "MeetsRequestedDate") &&
      !isPropHidden(ordersCustomization, "RequestedDeliveryAt"),
    redirectToItems:
      !isPropHidden(itemsCustomization, "MeetsRequestedDate") &&
      !isPropHidden(ordersCustomization, "RequestedDeliveryAt")
  },
  {
    id: "ConfirmedDeliveryDate",
    component: ConfirmedDeliveryDate,
    redirectToOrders: !isPropHidden(ordersCustomization, "ConfirmedDeliveryAt"),
    redirectToItems: !isPropHidden(itemsCustomization, "ConfirmedDeliveryAt")
  },
  {
    id: "PlannedDeliveryDate",
    component: PlannedDeliveryDate,
    redirectToOrders: !isPropHidden(ordersCustomization, "CalculatedConfirmDate"),
    redirectToItems: !isPropHidden(itemsCustomization, "CalculatedConfirmDate")
  },
  {
    id: "OrderAverageLeadTime",
    component: OrderAverageLeadTime,
    redirectToOrders: false,
    redirectToItems: false
  },
  {
    id: "ShipmentCount",
    component: ShipmentCount,
    redirectToOrders: false,
    redirectToItems: false
  },
  {
    id: "OrderLeadTime",
    component: OrderLeadTime,
    redirectToOrders: false,
    redirectToItems: false
  },
  {
    id: "DeliveryTimePrecision",
    component: DeliveryTimePrecision,
    redirectToOrders: true,
    redirectToItems: false
  },
  {
    id: "InternalOrderStatusBars",
    component: OverallInternalOrderStatusBars,
    redirectToOrders: !isPropHidden(internalOrdersCustomization, "DeliveryStatusLabel"),
    redirectToItems: !isPropHidden(internalItemsCustomization, "IPOItemDeliveryStatusLabel")
  },
  {
    id: "InternalOrderStatusDonuts",
    component: OverallInternalOrderStatusDonut,
    redirectToOrders: !isPropHidden(internalOrdersCustomization, "DeliveryStatusLabel"),
    redirectToItems: false
  },
  {
    id: "InventoryStatus",
    component: MaterialInventory,
    redirectToOrders: false,
    redirectToItems: false
  },
  {
    id: "ShipmentDelivered",
    component: ShipmentDelivered,
    redirectToOrders: false,
    redirectToItems: false
  }
];

const DashboardContainer = () => {
  const [state] = useStore();
  const [ordersViewProperties, setOrdersViewProperties] = useState<IViewProperty[]>([]);
  const [itemsViewProperties, setItemsViewProperties] = useState<IViewProperty[]>([]);
  const [internalOrdersViewProperties, setInternalOrdersViewProperties] = useState<IViewProperty[]>(
    []
  );
  const [internalItemsOrdersViewProperties, setInternalItemsOrdersViewProperties] = useState<
    IViewProperty[]
  >([]);
  const [dashboards, setDashboards] = useState(
    dashboardsConfig(
      ordersViewProperties,
      itemsViewProperties,
      internalOrdersViewProperties,
      internalItemsOrdersViewProperties
    )
  );
  const { getViewProperties } = useCustomizationActions();
  const { setOrdersRedirectionFilters } = useOrdersFiltersActions();
  const { setItemsRedirectionFilters } = usePOItemsFiltersActions();
  const { setInternalOrdersRedirectionFilters } = useInternalOrdersFiltersActions();
  const { setItemsRedirectionFilters: setInternalItemsRedirectionFilters } =
    useInternalItemsFiltersActions();
  const { localeString } = useLocalization();
  const navigate = useNavigate();
  const { currentCustomer } = useSettingsActions();

  const {
    ordersCustomization,
    itemsCustomization,
    internalOrdersCustomization,
    internalItemsCustomization
  } = state.fetchingData.viewProperties;

  const loaders = [
    ordersCustomization === undefined ? true : ordersCustomization,
    itemsCustomization === undefined ? true : itemsCustomization,
    internalOrdersCustomization === undefined ? true : internalOrdersCustomization,
    internalItemsCustomization === undefined ? true : internalItemsCustomization
  ];

  const { renderLoadingBar: loadingBar, showLoadingBar: loadingCustomizations } = useLoadingBar(
    loaders,
    true
  );

  const getCustomization = async () => {
    if (state.user) {
      const ordersCustomization = await getViewProperties(EViewId.PO, "ordersCustomization");
      const itemsCustomization = await getViewProperties(
        EViewId.PO_ITEMS_LIST,
        "itemsCustomization"
      );
      const internalOrdersCustomization = await getViewProperties(
        EViewId.INTERNAL_PO,
        "internalOrdersCustomization"
      );
      const internalItemsCustomization = await getViewProperties(
        EViewId.INTERNAL_ITEMS_LIST,
        "internalItemsCustomization"
      );

      setOrdersViewProperties(ordersCustomization);
      setItemsViewProperties(itemsCustomization);
      setInternalOrdersViewProperties(internalOrdersCustomization);
      setInternalItemsOrdersViewProperties(internalItemsCustomization);

      setDashboards(
        dashboardsConfig(
          ordersCustomization,
          itemsCustomization,
          internalOrdersCustomization,
          internalItemsCustomization
        )
      );
    }
  };

  useEffect(() => {
    getCustomization();
  }, []);

  const handleItemSelected = (
    redirectToOrders: boolean,
    redirectToItems: boolean
  ): Function | undefined => {
    return redirectToItems || redirectToOrders
      ? (itemData: IDashboardItemSelected) => {
          switch (itemData.graph) {
            case DashboardGraph.Orders:
              if (redirectToOrders) {
                setOrdersRedirectionFilters(itemData.filters);
                navigate("/orders");
              }
              break;
            case DashboardGraph.Items:
              if (redirectToItems) {
                setItemsRedirectionFilters(itemData.filters);
                navigate("/purchase-order-items");
              }
              break;
            case DashboardGraph.InternalOrders:
              if (redirectToOrders) {
                setInternalOrdersRedirectionFilters(itemData.filters);
                navigate("/internal-orders");
              }
              break;
            case DashboardGraph.InternalItems:
              if (redirectToItems) {
                setInternalItemsRedirectionFilters(itemData.filters);
                navigate("/internal-items");
              }
              break;
            default:
              return;
          }
        }
      : undefined;
  };

  const dashboardsByRow = (
    dashboards: IDashboard[],
    customerId: string,
    labels?: { [key: string]: string | undefined }
  ) => {
    const result = [];
    let i;
    for (i = 0; i < dashboards.length; i += 2) {
      const leftDashboard = dashboards[i];
      const LeftDashboardTile = leftDashboard.component;
      let rightDashboard = undefined;
      let RightDashboardTile = undefined;
      if (i + 1 < dashboards.length) {
        rightDashboard = dashboards[i + 1];
        RightDashboardTile = rightDashboard.component;
      }
      result.push(
        <Row key={`db_row_${i}`} className={cx({ half: dashboards.length > 2 })}>
          <LeftDashboardTile
            alignment={
              RightDashboardTile ? DashboardTileAlignment.LEFT : DashboardTileAlignment.FULL
            }
            labels={labels}
            customerId={customerId}
            onItemSelected={handleItemSelected(
              leftDashboard.redirectToOrders,
              leftDashboard.redirectToItems
            )}
          />
          {RightDashboardTile && (
            <RightDashboardTile
              alignment={DashboardTileAlignment.RIGHT}
              labels={labels}
              customerId={customerId}
              onItemSelected={handleItemSelected(
                rightDashboard ? rightDashboard.redirectToOrders : false,
                rightDashboard ? rightDashboard.redirectToItems : false
              )}
            />
          )}
        </Row>
      );
    }

    return result;
  };

  const dashboardLabels = (views: string[]) => {
    return {
      purchaseOrders: views.some(v => lowerCaseInitial(v) === "purchaseOrders")
        ? localeString("view_po")
        : undefined,
      purchaseOrderItems: views.some(v => lowerCaseInitial(v) === "purchaseOrderItemsList")
        ? localeString("view_po_it")
        : undefined,
      internalPurchaseOrders: views.some(v => lowerCaseInitial(v) === "internalPurchaseOrders")
        ? localeString("view_internal_po")
        : undefined,
      internalPurchaseOrderItems: views.some(
        v => lowerCaseInitial(v) === "internalPurchaseOrderItemsList"
      )
        ? localeString("view_internal_po_it")
        : undefined
    };
  };

  const layoutDashboards = (dashboards: IDashboard[], customerId: string, views: string[]) => {
    if (dashboards.length > 1) {
      return <>{dashboardsByRow(dashboards, customerId, dashboardLabels(views))}</>;
    } else {
      const { component: DashboardTile, redirectToOrders, redirectToItems } = dashboards[0];

      return (
        <DashboardTile
          className="column"
          lg={12}
          labels={dashboardLabels(views)}
          customerId={customerId}
          onItemSelected={handleItemSelected(redirectToOrders, redirectToItems)}
        />
      );
    }
  };

  const { identifier, features } = currentCustomer();
  const customerId = identifier ? identifier.toLowerCase() : "default";

  const currentCustomerDashboards = () => {
    const features = currentCustomer().features;
    return [...features.dashboards].reduce((current: IDashboard[], dashboardId: string) => {
      const dashboard = dashboards.find(d => d.id === dashboardId);
      if (!dashboard) {
        return current;
      }
      return [...current, dashboard];
    }, []);
  };

  const customerDashboards = currentCustomerDashboards();

  const noDashboardsActivated = () => {
    return (
      <div className="dashboard-no-boards">
        <p>No dashboards activated</p>
      </div>
    );
  };

  return (
    <ApplicationLayer title={localeString("MenuKeys_Dashboard")} menu={useAppMenu()}>
      {loadingCustomizations ? (
        loadingBar()
      ) : (
        <div className={`dashboard-wrapper ${customerDashboards.length <= 1 ? "full-height" : ""}`}>
          {itemsViewProperties.length && customerDashboards.some((c: IDashboard) => c)
            ? layoutDashboards(customerDashboards, customerId, features.views)
            : noDashboardsActivated()}
        </div>
      )}
    </ApplicationLayer>
  );
};

export default DashboardContainer;
